import {memo, useMemo} from "react";
import {ID, Timeframe, UnitType} from "@models";
import {iSeries} from "@components/TimeframesChart/TimeframesChart";
import {FCX} from "@datassential/platform-ui-lib";
import {getFormattedThousand} from "@core/utils/number";
import {useUserContext} from "@core/UserContext";
import BadgeDownloadPptButton from "@components/ButtonClipboardCopy/BadgeDownloadPptButton";
import Loader from "@components/Loader";
import TimeframesChart from "@components/TimeframesChart";
import DataFallback from "@components/DataFallback";
import {iChainHistoricTrendItem} from "@api/useChainsHistoricTrend";
import cn from "classnames";

const AnalyzePerformanceChart: FCX<{
    isLoading: boolean;
    isPptLoading: boolean;
    loadPptReport: () => Promise<void>;
    data: iChainHistoricTrendItem[] | null;
    chartData: iSeries[];
    removeChartItem: (id: ID) => void;
    selectedPeriod: Timeframe;
    selectedUnit: UnitType;
    selectedMetricId: ID;
    title: string;
}> = ({
    isLoading,
    isPptLoading,
    loadPptReport,
    data,
    chartData,
    removeChartItem,
    selectedPeriod,
    selectedUnit,
    selectedMetricId,
    title,
    className,
    testId,
}) => {
    const {fullAccess} = useUserContext();

    const noAccess = useMemo(
        () => selectedUnit === UnitType.Pct && !fullAccess,
        [selectedUnit, fullAccess]
    )

    return (
        <div
            key={`${selectedUnit}-${selectedPeriod}-${selectedMetricId}`}
            className={
                cn(
                    "AnalyzePerformanceChart",
                    className
                )
            }
            data-testid={testId}
        >
            {!isLoading && !noAccess && (
                <BadgeDownloadPptButton
                    style={{top: 40}}
                    isBusy={isPptLoading}
                    onClick={() => loadPptReport()}
                />
            )}
            {(isLoading || data === null) && (
                <Loader/>
            )}
            {!isLoading && data !== null && chartData.length > 0 && (
                <TimeframesChart
                    data={chartData}
                    timeframe={selectedPeriod}
                    hasYAxis
                    skipNullValues={selectedUnit === UnitType.Pct}
                    valueFormatter={(value: number) => selectedUnit === UnitType.Pct
                        ? `${Math.round(value)}%`
                        : `${getFormattedThousand(value)}`
                    }
                    valueSuffix={selectedUnit === UnitType.Pct ? '%' : ''}
                    removeSeries={removeChartItem}
                />
            )}
            {!isLoading && data !== null && chartData.length === 0 && (
                <DataFallback
                    title={title}
                    subTitle="Please select at least one item from the table below"
                />
            )}
            {!isLoading && data?.length === 0 && (
                <DataFallback
                    title="No results"
                />
            )}
        </div>
    )
}

export default memo(AnalyzePerformanceChart);